import { RedoOutlined, SendOutlined, WarningOutlined } from '@ant-design/icons';

import { DataTable, MenuThaoTac } from '../../../components/shared';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Empty, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { QUYEN, TRANG_THAI_GIAO } from '../constants';
import { useGhiUngDung, useLuotGiaoWebhook, useSuDungUngDung } from '../hooks/useCauHinhEkyc';
import type { LuotGiaoWebhook, UngDung } from '../types';

function O({ nhan, so, phu }: { nhan: string; so: string; phu?: string }) {
  return (
    <div className="min-w-0 rounded border border-default bg-subtle px-3 py-2">
      <p className="truncate text-2xs text-content-muted">{nhan}</p>
      <p className="text-lg font-semibold tabular-nums text-content-primary">{so}</p>
      {phu && <p className="truncate text-2xs text-content-muted">{phu}</p>}
    </div>
  );
}

/**
 * Số liệu 30 ngày và lượt giao webhook của một ứng dụng.
 *
 * Lượt giao cố ý hiện **URL đã chốt lúc gửi**, không URL hiện tại của ứng dụng: đổi URL sau khi sự kiện đã chuyển
 * sang hàng giao không đổi đích của lượt ấy, và màn phải nói đúng nơi máy chủ đã gõ cửa.
 */
export function ChiTietUngDung({ ungDung }: { ungDung: UngDung }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const quyen = useModulePermissions(QUYEN);
  const suDung = useSuDungUngDung(ungDung.id);
  const giao = useLuotGiaoWebhook(ungDung.id);
  const ghi = useGhiUngDung();
  const so = (n: number) => soVN(n, locale);

  const cot = [
    {
      title: t('ekyc.ungDung.cotSuKien'),
      dataIndex: 'suKien',
      key: 'suKien',
      render: (_: unknown, w: LuotGiaoWebhook) => (
        <div className="min-w-0">
          <p className="truncate font-mono text-xs text-content-primary">{w.suKien}</p>
          <p className="truncate text-2xs text-content-muted">{w.referenceId || w.phienId}</p>
        </div>
      ),
    },
    {
      title: t('chung.trangThai'),
      dataIndex: 'trangThai',
      key: 'trangThai',
      render: (_: unknown, w: LuotGiaoWebhook) => {
        const tong = TRANG_THAI_GIAO[w.trangThai];
        return (
          <div className="space-y-0.5">
            <Badge variant={tong.variant}>{t(tong.khoa)}</Badge>
            {w.guiTiepLuc && (
              <p className="text-2xs text-content-muted">{t('ekyc.ungDung.thuLaiLuc', { luc: ngayGioVN(w.guiTiepLuc) })}</p>
            )}
          </div>
        );
      },
    },
    {
      title: t('ekyc.ungDung.cotLanCuoi'),
      key: 'lanCuoi',
      render: (_: unknown, w: LuotGiaoWebhook) => {
        const cuoi = w.lichSu.at(-1);
        if (!cuoi) return <span className="text-content-muted">{KHONG_CO}</span>;
        return (
          <div className="min-w-0">
            <p className="text-sm text-content-secondary">
              {cuoi.status === null ? t('ekyc.ungDung.khongToiDich') : `HTTP ${cuoi.status}`}
              {' · '}
              {t('ekyc.ungDung.lanThu', { so: w.soLanThu })}
            </p>
            {cuoi.loi && <p className="max-w-xs truncate text-2xs text-content-muted" title={cuoi.loi}>{cuoi.loi}</p>}
          </div>
        );
      },
    },
    {
      title: t('ekyc.ungDung.cotTaoLuc'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_: unknown, w: LuotGiaoWebhook) => (
        <span className="whitespace-nowrap text-content-secondary">{ngayGioVN(w.createdAt)}</span>
      ),
    },
    {
      title: <span className="sr-only">{t('chung.thaoTac')}</span>,
      key: 'thaoTac',
      width: 56,
      render: (_: unknown, w: LuotGiaoWebhook) => (
        <MenuThaoTac
          tenBanGhi={w.suKien}
          muc={[
            {
              khoa: 'gui-lai',
              nhan: t('ekyc.ungDung.guiLai'),
              moTa: t('ekyc.ungDung.guiLaiMo'),
              icon: <RedoOutlined />,
              hien: quyen.ungDungSua && w.trangThai === 'FAILED',
              onChon: () => ghi.thuLai.mutate(w.id, {
                onSuccess: () => {
                  toast.success(t('ekyc.ungDung.daXepGuiLai'));
                  void giao.refetch();
                },
                onError: (e) => toast.error(dichLoi(e)),
              }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('ekyc.ungDung.soLieuTieuDe', { ten: ungDung.ten })}</CardTitle>
        </CardHeader>
        <CardBody>
          {suDung.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : suDung.isError ? (
            <p role="alert" className="text-sm text-danger-700">{t('ekyc.ungDung.loiSoLieu', { loi: dichLoi(suDung.error) })}</p>
          ) : suDung.data ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
              <O nhan={t('ekyc.ungDung.tongPhien')} so={so(suDung.data.tong)} />
              <O nhan={t('ekyc.ungDung.daDuyet')} so={so(suDung.data.ketCuc.daDuyet)} />
              <O nhan={t('ekyc.ungDung.tuChoi')} so={so(suDung.data.ketCuc.tuChoi)} />
              <O nhan={t('ekyc.ungDung.choDuyet')} so={so(suDung.data.ketCuc.choDuyet)} />
              <O nhan={t('ekyc.ungDung.roi')} so={so(suDung.data.ketCuc.roi)} />
              <O nhan={t('ekyc.ungDung.dangLam')} so={so(suDung.data.ketCuc.dangLam)} />
              <O
                nhan={t('ekyc.ungDung.webhookDaGui')}
                so={so(suDung.data.webhook.daGui)}
                phu={t('ekyc.ungDung.webhookChoVaLoi', { cho: so(suDung.data.webhook.choGui), loi: so(suDung.data.webhook.thatBai) })}
              />
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('ekyc.ungDung.luotGiaoTieuDe')}</CardTitle>
        </CardHeader>
        <CardBody>
          {giao.isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : giao.isError ? (
            <div role="alert" className="flex flex-wrap items-center gap-3 rounded border border-danger-200 bg-danger-50 px-3 py-2.5">
              <WarningOutlined className="shrink-0 text-danger-600" />
              <span className="min-w-0 text-sm text-danger-700">{t('ekyc.ungDung.loiLuotGiao', { loi: dichLoi(giao.error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void giao.refetch()}>{t('chung.thuLai')}</Button>
            </div>
          ) : (giao.data?.items.length ?? 0) === 0 ? (
            <Empty
              icon={SendOutlined}
              title={t('ekyc.ungDung.chuaCoLuotGiao')}
              description={ungDung.webhookUrl ? t('ekyc.ungDung.chuaCoLuotGiaoMo') : t('ekyc.ungDung.chuaDatWebhookMo')}
            />
          ) : (
            <DataTable<LuotGiaoWebhook>
              caption={t('ekyc.ungDung.bangLuotGiao')}
              columns={cot}
              dataSource={giao.data!.items}
              pagination={false}
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
